import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import QueueService from './QueueService';
import BroadcastModal from './BroadcastModal';
import CustomerHistoryModal from './CustomerHistoryModal';
import OutletBadge from './OutletBadge';
import './UsersPage.css';

const DEFAULT_PAGE_SIZE = 50;

const RATING_OPTIONS = [
  { label: '5 Star', value: '5 star' },
  { label: '4 Star', value: '4 star' },
  { label: '3 Star or Less', value: '3 star or less' }
];

const UsersPage = ({ token, business_id, businessName }) => {
  const [brandId, setBrandId] = useState(null);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedPhones, setSelectedPhones] = useState(new Set());
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [resolvingHistory, setResolvingHistory] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Filters
  const [minVisits, setMinVisits] = useState('');
  const [maxVisits, setMaxVisits] = useState('');
  const [minAvgSpend, setMinAvgSpend] = useState('');
  const [maxAvgSpend, setMaxAvgSpend] = useState('');
  const [recencyDays, setRecencyDays] = useState('');
  const [ratingIn, setRatingIn] = useState(''); // comma separated string
  // const [usedRewards, setUsedRewards] = useState(''); // Removed
  const [minAvgRating, setMinAvgRating] = useState('');
  const [badges, setBadges] = useState(''); // comma separated string
  const [sinceDays, setSinceDays] = useState(365);
  const [businessIds, setBusinessIds] = useState(''); // comma separated

  // Rating dropdown
  const [showRatingDropdown, setShowRatingDropdown] = useState(false);
  const ratingDropdownRef = useRef(null);

  // Broadcast modal state
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [modalContacts, setModalContacts] = useState([]);

  // Initialize brand_id from URL if provided, otherwise fetch from business info
  useEffect(() => {
    const urlBrand = searchParams.get('brand_id');
    if (urlBrand) {
      const parsed = Number(urlBrand);
      if (!Number.isNaN(parsed) && parsed > 0) {
        setBrandId(parsed);
        return; // no need to fetch
      }
    }
    const fetchBrandId = async () => {
      try {
        const response = await QueueService.GetBusinessInfo(business_id);
        if (response && response.brand_id) {
          setBrandId(response.brand_id);
        }
      } catch (e) {
        console.error('Failed to load business info:', e);
      }
    };
    if (business_id) fetchBrandId();
  }, [business_id, searchParams]);

  const payload = useMemo(() => {
    const p = {
      brand_id: brandId ? Number(brandId) : undefined,
      min_visits: minVisits !== '' ? Number(minVisits) : undefined,
      max_visits: maxVisits !== '' ? Number(maxVisits) : undefined,
      min_avg_spend: minAvgSpend !== '' ? Number(minAvgSpend) : undefined,
      max_avg_spend: maxAvgSpend !== '' ? Number(maxAvgSpend) : undefined,
      recency_days: recencyDays !== '' ? Number(recencyDays) : undefined,
      rating_in: ratingIn.trim() ? ratingIn.split(',').map(s => s.trim()).filter(Boolean) : undefined,
      // used_rewards: usedRewards === '' ? undefined : usedRewards === 'true',
      min_avg_rating: minAvgRating !== '' ? Number(minAvgRating) : undefined,
      badges: badges.trim() ? badges.split(',').map(s => s.trim()).filter(Boolean) : undefined,
      since_days: sinceDays === '' ? 365 : Number(sinceDays),
      page: page,
      page_size: pageSize,
    };
    if (businessIds.trim()) {
      const ids = businessIds.split(',').map(s => Number(s.trim())).filter(n => !Number.isNaN(n));
      if (ids.length) p.business_ids = ids;
    }
    return p;
  }, [brandId, minVisits, maxVisits, minAvgSpend, maxAvgSpend, recencyDays, ratingIn, minAvgRating, badges, sinceDays, page, pageSize, businessIds]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await QueueService.fetchCustomersFiltered(payload, token);
      const dataItems = result?.data?.items || [];

      setItems(prev => {
        if (payload.page === 1) return dataItems;
        return [...prev, ...dataItems];
      });

      setTotal(result?.data?.total || 0);
      setHasMore(!!result?.data?.has_more);
    } catch (e) {
      setError(e.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token || !brandId) return; // wait until brandId loads
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, brandId, page, pageSize]);

  const resetToFirstPageAndFetch = () => {
    if (page === 1) {
      fetchUsers();
    } else {
      setPage(1);
    }
  };

  const formatDateShort = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    const opts = { day: '2-digit', month: 'short', year: '2-digit' };
    return d.toLocaleDateString('en-GB', opts).replace(',', '').replace(/\s0/, ' ');
  };

  const formatFullDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { hour12: true });
  };

  const toggleSelect = (phone) => {
    setSelectedPhones(prev => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone); else next.add(phone);
      return next;
    });
  };

  const selectAllOnPage = () => {
    const phones = items.map(i => i.phone_number).filter(Boolean);
    setSelectedPhones(new Set(phones));
  };

  const clearSelection = () => setSelectedPhones(new Set());

  const openBroadcast = () => {
    if (selectedPhones.size === 0) return;

    const selectedUsers = items.filter(item => selectedPhones.has(item.phone_number));

    const contactsList = selectedUsers.map((user, idx) => {
      const fullPhone = `${user.country_code}${user.phone_number}`; // append country code

      return {
        id: `user-${idx}-${fullPhone}`,
        name: user.name || fullPhone,
        phone: fullPhone
      };
    });

    setModalContacts(contactsList);
    setShowBroadcastModal(true);
  };


  // Close rating dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ratingDropdownRef.current && !ratingDropdownRef.current.contains(event.target)) {
        setShowRatingDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleRatingOption = (optionValue) => {
    const currentSet = new Set(ratingIn.trim() ? ratingIn.split(',').map(s => s.trim()) : []);
    const valuesToToggle = optionValue.split(',');

    // Check if checks are already present (logic: check if the first value of group is present)
    const isSelected = valuesToToggle.every(v => currentSet.has(v));

    if (isSelected) {
      valuesToToggle.forEach(v => currentSet.delete(v));
    } else {
      valuesToToggle.forEach(v => currentSet.add(v));
    }

    setRatingIn(Array.from(currentSet).join(','));
  };

  const isRatingOptionSelected = (optionValue) => {
    if (!ratingIn.trim()) return false;
    const currentSet = new Set(ratingIn.split(',').map(s => s.trim()));
    const valuesToCheck = optionValue.split(',');
    return valuesToCheck.every(v => currentSet.has(v));
  };

  const handleCloseModal = () => {
    setShowBroadcastModal(false);
    setModalContacts([]);
  };

  const handleOpenHistoryByName = async (customerId) => {
    if (!customerId) {
      console.warn('No customer_id available');
      return;
    }
    setSelectedCustomerId(customerId);
    setShowHistoryModal(true);
  };

  const ctaHref = useMemo(() => `/campaigns?brand_id=${brandId || ''}`, [brandId]);

  const isAllSelected = items.length > 0 && items.every(i => selectedPhones.has(i.phone_number));

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      clearSelection();
    } else {
      selectAllOnPage();
    }
  };

  return (
    <div className="users-page">
      <div className="users-header">
        <div className="users-title-outlet-group">
          <h2 style={{ margin: 0 }}>Users</h2>
          <OutletBadge businessName={businessName} businessId={business_id} pageTitle="Users" />
        </div>
        <a className="users-cta" href={ctaHref}>Open Campaigns</a>
      </div>

      <div className="users-content">
        <div className="users-filters-card">
          <div className="filters-header">
            <h3>Filters</h3>
            <button className="apply-filters-btn" onClick={resetToFirstPageAndFetch} disabled={loading || !brandId}>
              Apply Filters
            </button>
          </div>

          <div className="filters-grid">
            <div className="field">
              <label>Brand ID</label>
              <input type="number" value={brandId || ''} onChange={e => setBrandId(e.target.value ? Number(e.target.value) : null)} />
            </div>
            <div className="field">
              <label>Business IDs</label>
              <input type="text" placeholder="e.g. 2,5,7" value={businessIds} onChange={e => setBusinessIds(e.target.value)} />
            </div>
            <div className="field">
              <label>Min Visits</label>
              <input type="number" min="0" value={minVisits} onChange={e => setMinVisits(e.target.value)} />
            </div>
            <div className="field">
              <label>Max Visits</label>
              <input type="number" min="0" value={maxVisits} onChange={e => setMaxVisits(e.target.value)} />
            </div>
            <div className="field">
              <label>Recency (Days)</label>
              <input type="number" min="0" value={recencyDays} onChange={e => setRecencyDays(e.target.value)} />
            </div>
            <div className="field">
              <label>Min Avg Spend</label>
              <input type="number" min="0" step="0.01" value={minAvgSpend} onChange={e => setMinAvgSpend(e.target.value)} />
            </div>
            <div className="field">
              <label>Max Avg Spend</label>
              <input type="number" min="0" step="0.01" value={maxAvgSpend} onChange={e => setMaxAvgSpend(e.target.value)} />
            </div>
            <div className="field">
              <label>Ratings</label>
              <div className="custom-dropdown" ref={ratingDropdownRef}>
                <div className="dropdown-trigger" onClick={() => setShowRatingDropdown(!showRatingDropdown)}>
                  {ratingIn ? `${ratingIn.split(',').length} selected` : 'Select ratings'}
                  <span style={{ fontSize: '10px', marginLeft: '6px' }}>▼</span>
                </div>
                {showRatingDropdown && (
                  <div className="up_dropdown-menu">
                    {RATING_OPTIONS.map((opt) => (
                      <div key={opt.label} className="dropdown-item" onClick={() => toggleRatingOption(opt.value)}>
                        <input
                          type="checkbox"
                          checked={isRatingOptionSelected(opt.value)}
                          readOnly
                        />
                        <span>{opt.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="field">
              <label>Min Avg Rating</label>
              <input type="number" min="0" max="5" step="0.1" value={minAvgRating} onChange={e => setMinAvgRating(e.target.value)} />
            </div>
            <div className="field">
              <label>Badges</label>
              <input type="text" placeholder="e.g. gold" value={badges} onChange={e => setBadges(e.target.value)} />
            </div>
            <div className="field">
              <label>Since (Days)</label>
              <input type="number" min="1" value={sinceDays} onChange={e => setSinceDays(e.target.value)} />
            </div>
            <div className="field">
              <label>Page Size</label>
              <input type="number" min="1" max="500" value={pageSize} onChange={e => setPageSize(Number(e.target.value) || DEFAULT_PAGE_SIZE)} />
            </div>
          </div>
        </div>

        {error && <div className="users-error">{error}</div>}

        <div className="users-table-card">
          <div className="table-actions-bar">
            <div className="table-stats">
              <span className="users-total">Total Users: <strong>{total}</strong></span>
              <span className="users-selected">Selected: <strong>{selectedPhones.size}</strong></span>
            </div>
            <div className="table-actions">
              <button className="action-btn secondary" onClick={selectAllOnPage} disabled={loading || items.length === 0}>Select Page</button>
              <button className="action-btn secondary" onClick={clearSelection} disabled={selectedPhones.size === 0}>Clear Selection</button>
              <button className="action-btn primary" onClick={openBroadcast} disabled={selectedPhones.size === 0}>
                Broadcast WhatsApp
              </button>
            </div>
          </div>

          <div className="users-table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAllToggle}
                      disabled={loading || items.length === 0}
                    />
                  </th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Visits</th>
                  <th>Last Visit</th>
                  <th>Outlets</th>
                  <th>Total Bill</th>
                  <th>Avg Spend</th>
                  {/* <th>Rewards (E/U)</th> */}
                  <th>Min Rating</th>
                  <th>Feedback</th>
                  <th>WA Opt-in</th>
                  <th>Last WA</th>
                  <th>Source</th>
                  <th>Tags</th>
                  <th>Created On</th>
                </tr>
              </thead>
              <tbody>
                {/* Only show "Loading..." comfortably if we have NO items. 
                    If we have items, we keep them visible while loading more. */}
                {loading && items.length === 0 ? (
                  <tr><td colSpan="15" className="loading-cell">Loading data...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="15" className="empty-cell">No users found matching criteria</td></tr>
                ) : (
                  <>
                    {items.map((u, idx) => (
                      <tr key={`${u.phone_number}-${idx}`} className={selectedPhones.has(u.phone_number) ? 'users-row selected' : 'users-row'}>
                        <td style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={selectedPhones.has(u.phone_number)} onChange={() => toggleSelect(u.phone_number)} />
                        </td>
                        <td
                          className="clickable-name"
                          onClick={() => handleOpenHistoryByName(u.customer_id)}
                        >
                          {u.name || '-'}
                        </td>
                        <td>{u.phone_number || '-'}</td>
                        <td>{u.visits ?? '-'}</td>
                        <td title={formatFullDateTime(u.last_visit_at)}>{formatDateShort(u.last_visit_at)}</td>
                        <td>{u.outlet_visits_count ?? '-'}</td>
                        <td>{typeof u.total_bill_amount === 'number' ? u.total_bill_amount.toFixed(2) : '-'}</td>
                        <td>{typeof u.avg_spend_per_visit === 'number' ? u.avg_spend_per_visit.toFixed(2) : '-'}</td>
                        {/* <td>{u.total_rewards_earned ?? 0}/{u.total_rewards_used ?? 0}</td> */}
                        <td>{u.min_rating ?? '-'}</td>
                        <td>{u.lifetime_feedback_score ?? '-'}</td>
                        <td>{u.whatsapp_opt_in === null || u.whatsapp_opt_in === undefined ? '-' : (u.whatsapp_opt_in ? 'Yes' : 'No')}</td>
                        <td title={formatFullDateTime(u.last_whatsapp_interaction_at)}>{formatDateShort(u.last_whatsapp_interaction_at)}</td>
                        <td>{u.source || '-'}</td>
                        <td>{Array.isArray(u.tags) && u.tags.length ? u.tags.join(', ') : '-'}</td>
                        <td title={formatFullDateTime(u.created_on)}>{formatDateShort(u.created_on)}</td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>

          <div className="users-pagination text-center" style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
            {hasMore ? (
              <button
                className="page-btn primary"
                onClick={() => setPage(page + 1)}
                disabled={loading}
                style={{ minWidth: '200px', padding: '10px 20px', fontSize: '16px' }}
              >
                {loading ? 'Loading...' : 'Load More Users'}
              </button>
            ) : (
              items.length > 0 && <span className="no-more-data">No more users to load</span>
            )}
          </div>
        </div>
      </div>

      <BroadcastModal
        token={token}
        show={showBroadcastModal}
        onClose={handleCloseModal}
        initialContacts={modalContacts}
        showCsvImport={false}
      />

      {showHistoryModal && (
        <CustomerHistoryModal
          customerId={selectedCustomerId}
          token={token}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
};

export default UsersPage;


